/**
 * Optional live-camera one-shot inference. The tester points the rear
 * camera at any scene, hits "Capture", and we run the same canvas → JPEG
 * → <img> → MobileNet path the check-in detector uses on exactly one
 * frame, then append the resulting embedding to the report.
 *
 * Purpose: pair a static-JPEG result against a camera-path result on the
 * SAME device. The cosine sim between those two embeddings tells us how
 * much the live camera pipeline alone is shifting things on this device,
 * independent of cross-device differences.
 */
import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  onSnapshot(video: HTMLVideoElement): Promise<void>;
}

export function CameraSnapshot({ onSnapshot }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let active = true;
    const landscape =
      typeof window !== "undefined" &&
      window.matchMedia("(orientation: landscape)").matches;
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: landscape ? 1920 : 1080 },
          height: { ideal: landscape ? 1080 : 1920 },
        },
        audio: false,
      })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Camera unavailable");
      });
    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    setCapturing(true);
    try {
      await onSnapshot(video);
      setCount((c) => c + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Capture failed");
    } finally {
      setCapturing(false);
    }
  }, [onSnapshot]);

  return (
    <div style={s.wrap}>
      {error ? (
        <div style={s.err}>{error}</div>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={s.video}
            aria-label="Live camera"
          />
          <div style={s.row}>
            <button
              style={s.btn}
              onClick={() => void capture()}
              disabled={capturing}
            >
              {capturing ? "Capturing…" : `Capture frame${count > 0 ? ` (${count} so far)` : ""}`}
            </button>
            <span style={s.hint}>
              Each capture appends to the report with id <code>camera-snapshot-N</code>.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#000",
    borderRadius: 6,
    padding: 8,
    marginBottom: 16,
  },
  video: {
    width: "100%",
    maxHeight: 320,
    objectFit: "cover",
    background: "#000",
    borderRadius: 4,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  btn: {
    minHeight: 40,
    padding: "0 14px",
    borderRadius: 6,
    border: "none",
    background: "#c8102e",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  hint: {
    fontSize: 12,
    color: "#ccc",
  },
  err: {
    color: "#fff",
    background: "#a92e1f",
    padding: 8,
    borderRadius: 4,
    fontSize: 13,
  },
};
